import React from 'react';
import { Text, Link, Card, Form, FormField, Select, Button, Flex } from '@heartinternet/hi-ui';
import type { FreeAccount, ParkedDomain } from '../types';

const MANAGE_CGI = '/manage/manage.cgi';

interface AssignHostingFormProps {
  freeAccounts: FreeAccount[];
  showPark: boolean;
  parkedDomains: ParkedDomain[];
}

/**
 * "You Have a New Hosting Account" — assign a free hosting package to a parked
 * domain. Native POST to manage.cgi (submit=ASSOC); the CGI reads `manage`,
 * `account`, `domain`, `submit`. When the customer has no parked domains
 * (`showPark` false) the domain select is replaced with purchase/transfer copy.
 */
export const AssignHostingForm: React.FC<AssignHostingFormProps> = ({
  freeAccounts,
  showPark,
  parkedDomains,
}) => (
  <section className="hi-ui-manage-hosting-page__section">
    <Text variant="heading-2">You have a new hosting account</Text>
    <Text variant="body">
      You have a new web hosting account! Why not assign it to one of your available domain names
      now?
    </Text>
    <Card variant="surface" padding="md" className="hi-ui-manage-hosting-page__assign-card">
      <Form action={MANAGE_CGI} method="post">
        <input type="hidden" name="manage" value="hosting" />
        <input type="hidden" name="submit" value="ASSOC" />
        <Flex direction="row" gap="md" align="end" wrap>
          <FormField label="Hosting package" labelPosition="top">
            <Select name="account" aria-label="Hosting package">
              {freeAccounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </FormField>
          {showPark ? (
            <FormField label="Assign to" labelPosition="top">
              <Select name="domain" aria-label="Assign to domain">
                {parkedDomains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.domain}
                    {d.status === 'TRANSFER' ? ' (awaiting transfer)' : ''}
                  </option>
                ))}
              </Select>
            </FormField>
          ) : (
            <Text variant="body" inline>
              You do not have any parked domains to associate with this web hosting package, please{' '}
              <Link href="/manage/domain.cgi">purchase</Link> or{' '}
              <Link href="/manage/transfer.cgi">transfer</Link> a domain to access this feature.
            </Text>
          )}
          {/* No parked domain to assign to → disable rather than POST and hit EBADDOMAIN. */}
          <Button
            type="submit"
            variant="primary"
            disabled={!showPark || parkedDomains.length === 0}
          >
            Assign Now
          </Button>
        </Flex>
      </Form>
    </Card>
    <Text variant="body">
      If you can&apos;t yet transfer in/register the domain name you would like to use on your new
      web hosting package then you can setup a domain name reference,{' '}
      <Link href="/manage/soft_transfer.cgi">click here</Link> to do this.
    </Text>
  </section>
);
